import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faGraduationCap,
  faBook,
  faStar,
  faTrophy,
  faArrowTrendUp,
} from "@fortawesome/free-solid-svg-icons";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const FunFacts = ({ student_count, teacher_count, executed_exams, blog_count }) => {
  const [counts, setCounts] = useState({ students: 0, teachers: 0, exams: 0, articles: 0 });
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);

  const statsData = [
    {
      id: "students",
      icon: faUsers,
      label: "طالب مسجّل",
      sublabel: "ينمو شهرياً",
      count: student_count || 5000,
      accent: "#2563eb",
      soft: "#eff6ff",
    },
    {
      id: "teachers",
      icon: faGraduationCap,
      label: "معلم محترف",
      sublabel: "من نخبة المتخصصين",
      count: teacher_count || 120,
      accent: "#9333ea",
      soft: "#faf5ff",
    },
    {
      id: "exams",
      icon: faBook,
      label: "اختبار مُنفَّذ",
      sublabel: "بنتائج فورية ودقيقة",
      count: executed_exams || 15000,
      accent: "#059669",
      soft: "#f0fdf4",
    },
    {
      id: "articles",
      icon: faStar,
      label: "مقال تعليمي",
      sublabel: "في المجتمع المعرفي",
      count: blog_count || 300,
      accent: "#6366f1",
      soft: "#fffbeb",
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    const targets = {
      students: statsData[0].count,
      teachers: statsData[1].count,
      exams: statsData[2].count,
      articles: statsData[3].count,
    };
    const steps = 80;
    const intervalMs = 2400 / steps;
    const timers = Object.entries(targets).map(([key, target]) => {
      let current = 0;
      const inc = target / steps;
      const t = setInterval(() => {
        current += inc;
        if (current >= target) { current = target; clearInterval(t); }
        setCounts((prev) => ({ ...prev, [key]: Math.round(current) }));
      }, intervalMs);
      return t;
    });
    return () => timers.forEach(clearInterval);
  }, [inView]);

  return (
    <section className="ff-root" id="fun-facts" ref={sectionRef}>



      <div className="ff-container">

        {/* ── header ── */}
        <motion.div
          className="ff-header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div className="ff-eyebrow" variants={fadeUp} custom={0}>
            <span className="ff-eyebrow-dot" />
            إحصائياتنا
          </motion.div>
          <motion.h2 className="ff-title" variants={fadeUp} custom={1}>
            أرقام حقيقية تصنع <em>فرقاً تعليمياً</em>
          </motion.h2>
          <motion.p className="ff-subtitle" variants={fadeUp} custom={2}>
            نمو مستمر يعكس جودة المحتوى وثقة الطلاب والمعلمين في منصة امتحاني
          </motion.p>
        </motion.div>

        {/* ── stats panel ── */}
        <motion.div
          className="ff-panel"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
        >
          {statsData.map((stat, i) => (
            <motion.div
              key={stat.id}
              className="ff-stat"
              variants={fadeUp}
              custom={i}
            >
              {/* vertical separator */}
              {i > 0 && <div className="ff-sep" />}

              <div className="ff-stat-inner">
                {/* icon */}
                <div className="ff-icon-pill" style={{ background: stat.soft, color: stat.accent }}>
                  <FontAwesomeIcon icon={stat.icon} />
                </div>

                {/* number */}
                <div className="ff-count-row">
                  <span className="ff-count" style={{ color: stat.accent }}>
                    {counts[stat.id].toLocaleString()}
                  </span>
                  <span className="ff-plus" style={{ color: stat.accent }}>+</span>
                </div>

                <p className="ff-label">{stat.label}</p>

                <p className="ff-sublabel">
                  <FontAwesomeIcon
                    icon={faArrowTrendUp}
                    style={{ color: stat.accent, fontSize: "0.75rem" }}
                  />
                  {stat.sublabel}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* ── trust bar ── */}
        <motion.div
          className="ff-trust"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <FontAwesomeIcon icon={faTrophy} className="ff-trophy" />
          <span>منصة امتحاني — الخيار الأول للتعليم الإلكتروني في المنطقة</span>
        </motion.div>

      </div>

      <style>{`
        .ff-root {
            font-family: "Zain", sans-serif;
          direction: rtl;
          position: relative;
          padding: 120px 0 100px;
          background: #fff;
          overflow: hidden;
          isolation: isolate;
        }



        /* container */
        .ff-container {
          position: relative; z-index: 2;
          max-width: 1260px; margin: 0 auto; padding: 0 40px;
        }
        @media(min-width:1200px){ .ff-container { padding: 0 60px; } }

        /* header */
        .ff-header { text-align: center; margin-bottom: 72px; }

        .ff-eyebrow {
          display: inline-flex; align-items: center; gap: 10px;
          color: #6366f1; font-weight: 800; font-size: 0.82rem;
          letter-spacing: 0.15em; text-transform: uppercase; margin-bottom: 22px;
        }
        .ff-eyebrow-dot {
          width: 8px; height: 8px; border-radius: 50%; background: #6366f1;
          animation: ff-pulse 2.2s ease-in-out infinite;
        }
        @keyframes ff-pulse {
          0%,100%{ box-shadow: 0 0 0 0 rgba(232,146,10,0.45); }
          50%{ box-shadow: 0 0 0 9px rgba(232,146,10,0); }
        }
        .ff-title {
          font-size: clamp(2.2rem, 4vw, 3.6rem);
          font-weight: 900; color: #0f172a;
          margin: 0 0 18px; line-height: 1.2;
          letter-spacing: -0.025em;
        }
        .ff-title em {
          font-style: normal;
          background: linear-gradient(90deg, #6366f1 0%, #6366f1 55%, #6366f1 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .ff-subtitle {
          font-size: 1.08rem; color: #64748b; line-height: 1.85; margin: 0;
        }

        /* ── panel: one white pill, stats side by side ── */
        .ff-panel {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          margin-bottom: 36px;
          position: relative;
        }
        @media(max-width:900px){
          .ff-panel { grid-template-columns: repeat(2,1fr); }
        }
        @media(max-width:500px){
          .ff-panel { grid-template-columns: 1fr; }
        }

        /* stat cell */
        .ff-stat {
          position: relative;
          display: flex; align-items: stretch;
        }
        .ff-stat-inner {
          flex: 1;
          display: flex; flex-direction: column;
          align-items: center; text-align: center;
          padding: 52px 32px 48px;
          gap: 0;
          transition: background 0.25s;
        }
        .ff-stat:hover .ff-stat-inner {
          background: #fafbff;
        }

        /* vertical separator */
        .ff-sep {
          width: 1px; align-self: stretch;
          background: linear-gradient(
            to bottom,
            transparent 0%,
            rgba(15,23,42,0.07) 25%,
            rgba(15,23,42,0.07) 75%,
            transparent 100%
          );
          flex-shrink: 0;
        }
        @media(max-width:900px){
          /* hide separators on 2-col; let natural row borders do the work */
          .ff-sep { display: none; }
          .ff-stat-inner {
            border-bottom: 1px solid rgba(15,23,42,0.06);
          }
          /* last row — no border bottom */
          .ff-stat:nth-child(3) .ff-stat-inner,
          .ff-stat:nth-child(4) .ff-stat-inner { border-bottom: none; }
          /* right column — add left border as separator */
          .ff-stat:nth-child(even) .ff-stat-inner {
            border-right: 1px solid rgba(15,23,42,0.06);
            border-left: none;
          }
        }
        @media(max-width:500px){
          .ff-stat-inner { border-bottom: 1px solid rgba(15,23,42,0.06) !important; border-right: none !important; }
          .ff-stat:last-child .ff-stat-inner { border-bottom: none !important; }
        }

        /* icon pill */
        .ff-icon-pill {
          width: 58px; height: 58px; border-radius: 18px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1.5rem;
          margin-bottom: 26px;
          flex-shrink: 0;
        }

        /* count */
        .ff-count-row {
          display: flex; align-items: flex-start; gap: 1px;
          line-height: 1; margin-bottom: 10px;
        }
        .ff-count {
          font-size: clamp(2.6rem, 4vw, 3.8rem);
          font-weight: 900; letter-spacing: -0.045em;
        }
        .ff-plus {
          font-size: 1.7rem; font-weight: 900;
          margin-top: 5px;
        }

        .ff-label {
          font-size: 1rem; font-weight: 800;
          color: #1e293b; margin: 0 0 8px;
        }
        .ff-sublabel {
          font-size: 0.82rem; color: #94a3b8; font-weight: 600;
          margin: 0; display: flex; align-items: center;
          justify-content: center; gap: 6px;
        }

        /* trust bar */
        .ff-trust {
          display: flex; align-items: center; justify-content: center;
          gap: 10px; color: #94a3b8;
          font-size: 0.88rem; font-weight: 700; letter-spacing: 0.01em;
        }
        .ff-trophy { color: #6366f1; font-size: 1rem; }

        @media(max-width:768px){
          .ff-root { padding: 90px 0 64px; }
          .ff-container { padding: 0 20px; }
          .ff-header { margin-bottom: 52px; }
          .ff-stat-inner { padding: 38px 22px 34px; }
        }
      `}</style>
    </section>
  );
};

export default FunFacts;